import React, { useState, useEffect } from 'react';
import { Plus, Lightbulb, Lock, Save, Trash2, Image as ImageIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import { Field, Input, Textarea, FileInput, FormActions } from '../components/FormField';
import { useAuth } from '../../context/AuthContext';

const BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
const empty = { title: '', description: '', order: '', linkedCategory: '' };

function SectionCard({ title, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-4">
      <h2 className="font-semibold text-gray-700 text-sm border-b border-gray-100 pb-3">{title}</h2>
      {children}
    </div>
  );
}

export default function AdminSolutions() {
  const { can, user } = useAuth();
  const isMasterAdmin = user?.role === 'master_admin';
  const canCreate = can('solutions', 'create');
  const canEdit   = can('solutions', 'edit');
  const canDelete = can('solutions', 'delete');
  const [solutions, setSolutions] = useState([]);
  const [modal, setModal]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]     = useState(empty);
  const [file, setFile]     = useState(null);
  const [preview, setPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [allCategories, setAllCategories] = useState([]);

  const [heroDoc, setHeroDoc]   = useState(null);
  const [heroFile, setHeroFile] = useState(null);
  const [heroTitle, setHeroTitle] = useState('Sample Solutions');
  const [heroLoading, setHeroLoading] = useState(false);

  const [introForm, setIntroForm] = useState({ label: 'What We Do', heading: 'Comprehensive Sample Solutions', body: "From concept to delivery, House of Sample provides end-to-end sample solutions designed to elevate your brand's presentation and streamline your sales process." });
  const [introLoading, setIntroLoading] = useState(false);

  const [processForm, setProcessForm] = useState({
    label: 'How It Works', heading: 'Our Process',
    steps: [
      { icon: '💬', title: 'Consultation', desc: 'We understand your brand vision, requirements, and target audience to craft the perfect brief.' },
      { icon: '✏️', title: 'Design',       desc: 'Our designers create detailed mockups and prototypes tailored to your specifications.' },
      { icon: '⚙️', title: 'Production',   desc: 'Expert craftsmen bring your samples to life using premium materials and precision techniques.' },
      { icon: '🚀', title: 'Delivery',     desc: 'Quality-checked samples are carefully packaged and delivered to your doorstep on time.' },
    ],
  });
  const [processLoading, setProcessLoading] = useState(false);

  const [ctaForm, setCtaForm] = useState({ heading: 'Ready to Get Started?', body: 'Contact us today to discuss your sample requirements.', btnLabel: 'Get in Touch', btnLink: '/contact' });
  const [ctaLoading, setCtaLoading] = useState(false);

  const load = () => api.get('/solutions').then(r => setSolutions(r.data.solutions));

  useEffect(() => {
    load();
    api.get('/categories').then(r => setAllCategories(r.data.categories || [])).catch(() => {});
    api.get('/content?page=solutions').then(res => {
      const items = res.data.content || [];
      const find  = s => items.find(c => c.section === s);

      const h = find('hero');
      if (h) { setHeroDoc(h); if (h.title) setHeroTitle(h.title); }

      const intr = find('intro');
      if (intr) setIntroForm({ label: intr.subtitle || 'What We Do', heading: intr.title || 'Comprehensive Sample Solutions', body: intr.body || '' });

      const proc = find('process');
      if (proc) setProcessForm({
        label:   proc.subtitle || 'How It Works',
        heading: proc.title    || 'Our Process',
        steps:   proc.extraData?.steps?.length ? proc.extraData.steps : processForm.steps,
      });

      const c = find('cta');
      if (c) setCtaForm({ heading: c.title || 'Ready to Get Started?', body: c.body || '', btnLabel: c.ctaButtons?.[0]?.label || 'Get in Touch', btnLink: c.ctaButtons?.[0]?.link || '/contact' });
    }).catch(() => {});
  }, []);

  const saveHero = async (e) => {
    e.preventDefault();
    setHeroLoading(true);
    try {
      const fd = new FormData();
      fd.append('page', 'solutions'); fd.append('section', 'hero');
      fd.append('title', heroTitle);
      if (heroFile) fd.append('images', heroFile);
      const res = heroFile
        ? await api.post('/content/hero-images', (() => { const f = new FormData(); f.append('page','solutions'); f.append('section','hero'); f.append('images', heroFile); return f; })(), { headers: { 'Content-Type': 'multipart/form-data' } })
        : await api.post('/content', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setHeroDoc(res.data.content); setHeroFile(null);
      toast.success('Hero saved');
    } catch { toast.error('Error saving hero'); }
    finally { setHeroLoading(false); }
  };

  const removeHeroImage = async (idx) => {
    if (!heroDoc?._id) return;
    try { const res = await api.delete(`/content/${heroDoc._id}/images/${idx}`); setHeroDoc(res.data.content); toast.success('Image removed'); }
    catch { toast.error('Error'); }
  };

  const saveIntro = async (e) => {
    e.preventDefault(); setIntroLoading(true);
    try {
      const fd = new FormData();
      fd.append('page','solutions'); fd.append('section','intro');
      fd.append('title', introForm.heading); fd.append('subtitle', introForm.label); fd.append('body', introForm.body);
      await api.post('/content', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Intro saved');
    } catch { toast.error('Error'); } finally { setIntroLoading(false); }
  };

  const saveProcess = async (e) => {
    e.preventDefault(); setProcessLoading(true);
    try {
      const fd = new FormData();
      fd.append('page','solutions'); fd.append('section','process');
      fd.append('title', processForm.heading); fd.append('subtitle', processForm.label);
      fd.append('extraData', JSON.stringify({ steps: processForm.steps }));
      await api.post('/content', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Process saved');
    } catch { toast.error('Error'); } finally { setProcessLoading(false); }
  };

  const saveCta = async (e) => {
    e.preventDefault(); setCtaLoading(true);
    try {
      const fd = new FormData();
      fd.append('page','solutions'); fd.append('section','cta');
      fd.append('title', ctaForm.heading); fd.append('body', ctaForm.body);
      fd.append('ctaButtons', JSON.stringify([{ label: ctaForm.btnLabel, link: ctaForm.btnLink }]));
      await api.post('/content', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('CTA saved');
    } catch { toast.error('Error'); } finally { setCtaLoading(false); }
  };

  const openCreate = () => { setEditing(null); setForm(empty); setFile(null); setPreview(null); setErrors({}); setModal(true); };
  const openEdit = (s) => { setEditing(s); setForm({ title: s.title, description: s.description, order: s.order ?? '', linkedCategory: s.linkedCategory || '' }); setFile(null); setPreview(s.image || null); setErrors({}); setModal(true); };

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = 'Solution title is required';
    if (!form.description.trim()) e.description = 'Description is required';
    if (form.order !== '' && isNaN(Number(form.order))) e.order = 'Order must be a number';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length) { setErrors(e2); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title.trim());
      fd.append('description', form.description.trim());
      fd.append('order', form.order || 0);
      if (form.linkedCategory) fd.append('linkedCategory', form.linkedCategory);
      if (file) fd.append('image', file);
      let res;
      if (editing) {
        res = await api.put(`/solutions/${editing._id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        res = await api.post('/solutions', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      if (res.data.pending) {
        toast.info(res.data.message || 'Submitted for approval');
      } else {
        toast.success(editing ? 'Solution updated' : 'Solution created');
      }
      setModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this solution?')) return;
    await api.delete(`/solutions/${id}`); toast.success('Deleted'); load();
  };

  const columns = [
    { key: 'image', label: 'Image', isImage: true, sortable: false },
    { key: 'title', label: 'Title', isTitle: true },
    { key: 'description', label: 'Description',
      render: v => <span className="text-xs text-gray-400 line-clamp-2">{v}</span>
    },
    { key: 'order', label: 'Order',
      render: v => <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{v ?? '—'}</span>
    },
  ];

  return (
    <div className="space-y-6 pb-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Solutions</h1>
          <p className="text-sm text-gray-400 mt-0.5">{solutions.length} total solutions</p>
        </div>
        <button onClick={openCreate}
          disabled={!canCreate}
          title={!canCreate ? 'No permission to create solutions' : ''}
          className="flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-xl shadow-sm transition-all hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: canCreate ? 'linear-gradient(135deg, #B8A830, #d4af37)' : '#9CA3AF' }}>
          {canCreate ? <Plus size={14} /> : <Lock size={14} />} Add Solution
        </button>
      </div>

      {/* Page sections — master_admin only */}
      {isMasterAdmin && (
        <>
          <SectionCard title="Hero Section">
        <form onSubmit={saveHero} className="space-y-3">
          <Field label="Page Title"><Input value={heroTitle} onChange={e => setHeroTitle(e.target.value)} placeholder="Sample Solutions" /></Field>
          {heroDoc?.images?.length > 0 && (
            <div className="flex flex-wrap gap-3">
              {heroDoc.images.map((url, i) => (
                <div key={i} className="relative group">
                  <img src={url.startsWith('http') ? url : `${BASE}${url}`} alt="" className="w-32 h-20 object-cover rounded-lg border border-gray-200" />
                  <button type="button" onClick={() => removeHeroImage(i)}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 size={10} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center gap-3">
            <input type="file" accept="image/*" onChange={e => setHeroFile(e.target.files[0])} className="text-sm text-gray-600 flex-1" />
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={heroLoading} className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white text-sm rounded-lg hover:bg-yellow-700 disabled:opacity-50">
              <Save size={14} /> {heroLoading ? 'Saving...' : 'Save Hero'}
            </button>
          </div>
        </form>
      </SectionCard>

      <SectionCard title="Intro Section — What We Do">
        <form onSubmit={saveIntro} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Label (small text)"><Input value={introForm.label} onChange={e => setIntroForm(f => ({ ...f, label: e.target.value }))} placeholder="What We Do" /></Field>
            <Field label="Heading"><Input value={introForm.heading} onChange={e => setIntroForm(f => ({ ...f, heading: e.target.value }))} placeholder="Comprehensive Sample Solutions" /></Field>
          </div>
          <Field label="Description"><Textarea value={introForm.body} onChange={e => setIntroForm(f => ({ ...f, body: e.target.value }))} rows={3} /></Field>
          <div className="flex justify-end">
            <button type="submit" disabled={introLoading} className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white text-sm rounded-lg hover:bg-yellow-700 disabled:opacity-50">
              <Save size={14} /> {introLoading ? 'Saving...' : 'Save Intro'}
            </button>
          </div>
        </form>
      </SectionCard>

      <SectionCard title="Process Section — How It Works">
        <form onSubmit={saveProcess} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Label"><Input value={processForm.label} onChange={e => setProcessForm(f => ({ ...f, label: e.target.value }))} placeholder="How It Works" /></Field>
            <Field label="Heading"><Input value={processForm.heading} onChange={e => setProcessForm(f => ({ ...f, heading: e.target.value }))} placeholder="Our Process" /></Field>
          </div>
          <div className="space-y-3">
            {processForm.steps.map((step, i) => (
              <div key={i} className="border border-gray-100 rounded-xl p-3 space-y-2">
                <p className="text-xs font-semibold text-gray-500">Step {i + 1}</p>
                <div className="grid grid-cols-3 gap-2">
                  <Field label="Icon (emoji)"><Input value={step.icon} onChange={e => { const s=[...processForm.steps]; s[i]={...s[i],icon:e.target.value}; setProcessForm(f=>({...f,steps:s})); }} placeholder="💬" /></Field>
                  <Field label="Title" className="col-span-2"><Input value={step.title} onChange={e => { const s=[...processForm.steps]; s[i]={...s[i],title:e.target.value}; setProcessForm(f=>({...f,steps:s})); }} /></Field>
                </div>
                <Field label="Description"><Textarea value={step.desc} onChange={e => { const s=[...processForm.steps]; s[i]={...s[i],desc:e.target.value}; setProcessForm(f=>({...f,steps:s})); }} rows={2} /></Field>
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={processLoading} className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white text-sm rounded-lg hover:bg-yellow-700 disabled:opacity-50">
              <Save size={14} /> {processLoading ? 'Saving...' : 'Save Process'}
            </button>
          </div>
        </form>
      </SectionCard>

      <SectionCard title="CTA Section — Ready to Get Started?">
        <form onSubmit={saveCta} className="space-y-3">
          <Field label="Heading"><Input value={ctaForm.heading} onChange={e => setCtaForm(f => ({ ...f, heading: e.target.value }))} /></Field>
          <Field label="Description"><Textarea value={ctaForm.body} onChange={e => setCtaForm(f => ({ ...f, body: e.target.value }))} rows={2} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Button Label"><Input value={ctaForm.btnLabel} onChange={e => setCtaForm(f => ({ ...f, btnLabel: e.target.value }))} /></Field>
            <Field label="Button Link"><Input value={ctaForm.btnLink} onChange={e => setCtaForm(f => ({ ...f, btnLink: e.target.value }))} /></Field>
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={ctaLoading} className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white text-sm rounded-lg hover:bg-yellow-700 disabled:opacity-50">
              <Save size={14} /> {ctaLoading ? 'Saving...' : 'Save CTA'}
            </button>
          </div>
        </form>
      </SectionCard>
        </>
      )}

      <DataTable
        columns={columns}
        data={solutions}
        onEdit={canEdit ? openEdit : null}
        onDelete={canDelete ? handleDelete : null}
        searchKeys={['title', 'description']}
        defaultView="list"
      />

      {modal && (
        <Modal title={editing ? 'Edit Solution' : 'Add New Solution'} onClose={() => setModal(false)}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-700">
              {isMasterAdmin
                ? 'Solutions appear on the Sample Solutions page. Use display order to control sequence.'
                : 'Your solution will be submitted for Master Admin approval before it appears publicly.'}
            </div>
            <Field label="Solution Title" required error={errors.title}>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Custom Sample Books" error={errors.title} />
            </Field>
            <Field label="Description" required error={errors.description}>
              <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Describe this solution…" rows={4} error={errors.description} />
            </Field>
            <Field label="Display Order" hint="Lower numbers appear first" error={errors.order}>
              <Input type="number" min="0" value={form.order} onChange={e => setForm({ ...form, order: e.target.value })}
                placeholder="1" error={errors.order} />
            </Field>
            <Field label="Linked Product Category" hint="When clicked, card redirects to this category in Products">
              <select
                value={form.linkedCategory}
                onChange={e => setForm({ ...form, linkedCategory: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-yellow-400">
                <option value="">— No link (no redirect) —</option>
                {allCategories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
            </Field>
            <FileInput label="Solution Image"
              onChange={e => { setFile(e.target.files[0]); setPreview(e.target.files[0] ? URL.createObjectURL(e.target.files[0]) : null); }}
              preview={preview} />
            <FormActions onCancel={() => setModal(false)} loading={loading} submitLabel={editing ? 'Update Solution' : 'Add Solution'} />
          </form>
        </Modal>
      )}
    </div>
  );
}
